import { Form, message } from "antd";
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useRouter } from "next/router";
import {
  Step,
  Flow,
  FinancialBrand,
  StartStepAssets,
} from "@dreambigger/shared/src/types";
import { skipAheadKey, keyByLoginModeHash } from "../pages/flows/[flowId]";
import {
  LocalStorage,
  removeBrowserQueryParam,
  isModernTheme,
} from "@dreambigger/shared/src/utils";
import { useSegment } from "@dreambigger/shared/src/hooks";
import { AuthApi } from "@dreambigger/shared/src/api/acquire";
import { api } from "@dreambigger/shared/src/api/acquire";
import { useAuthToken } from "../passwordless-auth";
import { changeStepForward } from "../utils/changeStepForward";
import { notification } from "antd";
import useAuthCallback from "../hooks/use-auth-callback";
import getFingerprint from "@dreambigger/shared/src/utils/getFingerprint";
import { formatAsNationalPhoneNum } from "../utils/textFormatting";
import { useRecaptcha } from "@dreambigger/shared/src/hooks";
import { FlowRenderOptions } from "../pages/flows/[flowId]";
import { useApplication } from "../api";
import {
  DefaultTemplate,
  CirclesTemplate,
  TrianglesTemplate,
  SquaresTemplate,
  V2Template,
  CodeVerificationProps,
} from "./start-templates";

const LOGIN_RECAPTCHA_ACTION = "LOGIN";
const authApi = new AuthApi(api);

export type StartStepProps = {
  step: Step;
  brand: FinancialBrand;
  flow: Flow;
  renderOptions: FlowRenderOptions;
  isAdminMode?: boolean;
};

export default function Start({
  step,
  brand,
  flow,
  renderOptions,
  isAdminMode = false,
}: StartStepProps) {
  const theme = brand.theme || "v1";
  const { hideHeader = false } = renderOptions;
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [userId, setUserId] = useState("");
  const [loginId, setLoginId] = useState("");
  const [skipAhead, setSkipAhead] = useState(false);

  const applicationHelper = useApplication(flow.financialInstitution.id);
  const startResponse = useMemo(
    () =>
      isAdminMode && applicationHelper
        ? applicationHelper.findResponse(flow.id, "draft", step.slug)
        : undefined,
    [isAdminMode, applicationHelper, flow, step.slug]
  );
  const router = useRouter();
  const { otpId } = router.query;
  const assets: StartStepAssets = step.assets;
  const localStorage = new LocalStorage(flow.financialInstitution.id);
  const tokenHelper = useAuthToken(flow.financialInstitution.id);
  const [loginForm] = Form.useForm();
  const segment = useSegment();
  const keyByLoginMode =
    keyByLoginModeHash[flow.financialInstitution.loginMode];
  const cred = localStorage.getItem(keyByLoginMode);

  const getInitialValue = () => {
    if (isAdminMode && startResponse?.fields) {
      const responseValue = startResponse.fields[keyByLoginMode];
      if (responseValue) {
        return keyByLoginMode === "email"
          ? responseValue
          : formatAsNationalPhoneNum(responseValue.toString());
      }
    }
    return keyByLoginMode === "email" ? cred : formatAsNationalPhoneNum(cred);
  };
  const formattedCred = getInitialValue();
  const emailInput = useRef<any>(null);
  const phoneInput = useRef<any>(null);

  // Initialize recaptcha and lazy load the script
  const recaptchaSiteKey = flow.financialInstitution.recaptchaSiteKey;
  const recaptcha = useRecaptcha(recaptchaSiteKey);
  if (recaptchaSiteKey) {
    recaptcha.load();
  }

  const setFocus = useCallback(() => {
    if (flow.financialInstitution.loginMode === "email" && emailInput.current) {
      emailInput.current.focus();
      return;
    }
    if (phoneInput.current) {
      phoneInput.current.focus();
    }
  }, [emailInput, phoneInput]);

  useEffect(() => {
    notification.destroy();
  }, []);

  useEffect(() => {
    if (!otpId) {
      return;
    }

    setSkipAhead(true);
    removeBrowserQueryParam("otpId", router);

    if (flow.authenticationMode === "none") {
      guestLogin(otpId as string);
      return;
    }
    sendOtp(otpId as string);
    return;
  }, [flow, otpId]);

  const nextStep = () => {
    if (skipAhead) {
      localStorage.setItem(skipAheadKey, "true");
    }
    changeStepForward(assets.nextSlug);
  };

  const sendOtp = useCallback(
    (id: string) => {
      if (
        !tokenHelper.jwtIsExpired() &&
        id === localStorage.getItem(keyByLoginMode) &&
        tokenHelper.decodeJWT(tokenHelper.getJwtToken())?.user
          ?.authenticationMode === "otp"
      ) {
        nextStep();
        return;
      }

      setLoginId(id);

      getFingerprint()
        .then((visitorId) =>
          authApi.sendOtp({
            otpId: id,
            otpType: flow.financialInstitution.loginMode,
            userType: "acquire",
            financialInstitution: {
              id: flow.financialInstitution.id,
              name: flow.financialInstitution.name,
              notificationAssets: flow.financialInstitution.notificationAssets,
            },
            deviceFingerprint: visitorId,
          })
        )
        .then(({ data }) => {
          setUserId(data.user.id);
          setShowVerifyModal(true);
          segment.track({
            action: "Authentication",
            label: "OTP Sent",
            properties: {
              [keyByLoginMode]: id,
              flowId: flow.id,
            },
          });
        })
        .catch((error) => {
          segment.track({
            action: "Authentication",
            label: "OTP Send Failed",
            properties: {
              [keyByLoginMode]: id,
              flowId: flow.id,
              error: error.message,
            },
          });
          console.log(error);
        });
    },
    [nextStep]
  );

  const handleAuthentication = useAuthCallback(flow, keyByLoginMode);

  const guestLogin = useCallback(
    (id: string) => {
      if (
        !tokenHelper.jwtIsExpired() &&
        id === localStorage.getItem(keyByLoginMode)
      ) {
        nextStep();
        return;
      }

      Promise.all(
        recaptchaSiteKey
          ? [getFingerprint(), recaptcha.execute(LOGIN_RECAPTCHA_ACTION)]
          : [getFingerprint()]
      )
        .then(([visitorId, responseToken]) =>
          authApi.authenticateGuest({
            loginId: id,
            loginType: flow.financialInstitution.loginMode,
            financialInstitution: {
              id: flow.financialInstitution.id,
              name: flow.financialInstitution.name,
              notificationAssets: flow.financialInstitution.notificationAssets,
            },
            userType: "acquire",
            deviceFingerprint: visitorId,
            recaptcha: responseToken
              ? {
                  responseToken,
                  action: LOGIN_RECAPTCHA_ACTION,
                }
              : undefined,
          })
        )
        .then(({ data }) => {
          handleAuthentication(data.jwt, "Guest Authenticated");
          nextStep();
          return;
        })
        .catch((error) => {
          if (recaptchaSiteKey && error.response?.status === 401) {
            message.error(
              "Access denied. Please try from a different device or network."
            );
          }
          segment.track({
            action: "Authentication",
            label: "Guest Login Failed",
            properties: {
              [keyByLoginMode]: id,
              flowId: flow.id,
              error: error.message,
            },
          });
          console.log(error);
        });
    },
    [nextStep]
  );

  const handleNext = useCallback(
    (values: any) => {
      setSkipAhead(false);
      segment.track({ action: "Button Click", label: `Submit - ${step.slug}` });

      if (isAdminMode && assets.nextSlug) {
        changeStepForward(assets.nextSlug);
        return;
      }

      if (flow.authenticationMode === "none") {
        guestLogin(values[keyByLoginMode]);
        return;
      }
      sendOtp(values[keyByLoginMode]);
    },
    [sendOtp, isAdminMode, assets.nextSlug]
  );

  useEffect(() => {
    if (!showVerifyModal) {
      setTimeout(setFocus, 500);
    }
  }, [showVerifyModal]);

  useEffect(() => {
    if (isAdminMode && startResponse?.fields) {
      const responseValue = startResponse.fields[keyByLoginMode];
      if (responseValue) {
        const formattedValue =
          keyByLoginMode === "email"
            ? responseValue
            : formatAsNationalPhoneNum(responseValue.toString());
        loginForm.setFieldsValue({ [keyByLoginMode]: formattedValue });
      }
    }
  }, [isAdminMode, startResponse, keyByLoginMode, loginForm]);

  // Shared props for code verification modal
  const codeVerificationProps: CodeVerificationProps = {
    title: assets.otpModalHeaderText,
    headline: assets.otpModalTitle,
    description: assets.otpModalText,
    secondaryCta: assets.otpModalChangeIdText,
    visible: showVerifyModal,
    flow,
    keyByLoginMode,
    loginId,
    userId,
    onCancel: () => setShowVerifyModal(false),
    onSecondaryCtaClick: () => setShowVerifyModal(false),
    onSuccess: () => {
      setShowVerifyModal(false);
      nextStep();
    },
  };

  // Shared props for all templates
  const templateProps = {
    assets,
    brand,
    flow,
    hideHeader,
    loginForm,
    keyByLoginMode,
    formattedCred,
    emailInputRef: emailInput,
    phoneInputRef: phoneInput,
    handleNext,
    isAdminMode,
    codeVerificationProps,
  };

  // Render appropriate template based on theme. The start step keeps its hero
  // image under both modern themes (v2 and v3).
  if (isModernTheme(theme)) {
    return <V2Template {...templateProps} />;
  }

  // V1 templates
  switch (assets.template) {
    case "circles":
      return <CirclesTemplate {...templateProps} />;
    case "triangles":
      return <TrianglesTemplate {...templateProps} />;
    case "squares":
      return <SquaresTemplate {...templateProps} />;
    default:
      return <DefaultTemplate {...templateProps} />;
  }
}
